import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Badge,
  Spinner,
  Modal,
  Row,
  Col,
  Form,
  Pagination,
} from "react-bootstrap";
import {
  EyeFill,
  Search,
  GeoAltFill,
  ClockHistory,
  PersonBadge,
  FileText,
  Collection,
  ChevronLeft,
  ChevronRight,
} from "react-bootstrap-icons";
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Tooltip as MuiTooltip,
} from "@mui/material";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import API_BASE_URL from "../../../config/api";

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

/* ── Action icon button ── */
const ActionBtn = ({ tooltip, color, onClick, disabled, children }) => (
  <MuiTooltip title={tooltip} placement="top" arrow>
    <span>
      <Button
        variant="link"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="p-1 rounded-2"
        style={{
          color,
          lineHeight: 1,
          opacity: disabled ? 0.4 : 1,
          minWidth: "auto",
        }}
      >
        {children}
      </Button>
    </span>
  </MuiTooltip>
);

const statusVariant = {
  upcoming: "warning",
  live: "success",
  ended: "secondary",
};

const LogManagement = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [allLocations, setAllLocations] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionsData, setSessionsData] = useState([]);
  const [sessionPagination, setSessionPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [attendanceData, setAttendanceData] = useState([]);
  const [attendancePagination, setAttendancePagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [attLoading, setAttLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState([]);

  // ================= FETCH CLASSES =================
  const fetchClasses = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      let url = `${API_BASE_URL}/api/permanentDelete/getLogs?page=${page}&limit=${limit}`;

      if (search) url += `&search=${search}`;
      if (filterLocation) url += `&filterLocation=${filterLocation}`;
      if (filterStatus) url += `&classStatus=${filterStatus}`;

      const res = await axios.get(url);

      setData(res.data.data || []);
      setPagination({
        page: res.data?.pagination?.page,
        limit: res.data?.pagination?.limit,
        total: res.data?.pagination?.total,
      });
    } catch (err) {
      console.error("Error fetching class data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleShowCollections = (collections) => {
    setSelectedCollections(collections || []);
    setShowCollectionModal(true);
  };

  // ================= FETCH LOCATIONS =================
  const fetchAllLocations = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/location/admin/getAllLocations`,
      );
      setAllLocations(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  };

  useEffect(() => {
    fetchAllLocations();
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchClasses(pagination.page, pagination.limit);
  }, [search, filterLocation, filterStatus]);

  const handlePageChange = (page) => {
    fetchClasses(page, pagination.limit);
  };

  // ================= SHOW SESSIONS =================
  const handleShowSessions = async (cls, page = 1, limit = 10) => {
    try {
      setSelectedClass(cls);
      setShowSessionsModal(true);
      setSessionsLoading(true);

      const res = await axios.get(
        `${API_BASE_URL}/api/AdminClasses/getClassByIdAdmin/${cls._id}?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        },
      );

      setSessionsData(res.data?.data?.sessions || []);
      setSessionPagination({
        page: res.data?.page || page,
        limit: res.data?.limit || limit,
        total: res.data?.totalSessions || 0,
      });
    } catch (err) {
      console.error("Error fetching sessions:", err);
      setSessionsData([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleSessionChange = (page) => {
    if (selectedClass) {
      handleShowSessions(selectedClass, page, sessionPagination.limit);
    }
  };

  // ================= SHOW ATTENDANCE (FIXED) =================
  const handleShowAttendance = async (session, page = 1, limit = 10) => {
    try {
      setSelectedSession(session);
      setShowSessionsModal(false);
      setShowAttendanceModal(true);
      setAttLoading(true);

      setAttendancePagination((prev) => ({
        ...prev,
        page: page,
        limit: limit,
      }));

      const res = await axios.get(
        `${API_BASE_URL}/api/AdminClasses/getSessionAttendence/${selectedClass._id}/${session._id}?page=${page}&limit=${limit}`,
      );

      const registeredUsers = res.data?.data || [];
      const users = registeredUsers.map((user) => ({
        id: user.userId,
        name: user.name || "Unknown",
        email: user.email || "N/A",
        attendance: user.attendance || "Absent",
        isPrisoner: false,
      }));

      const prisonersRaw =
        res.data?.data?.instructorAttendance?.prisonerAttendance || [];

      const prisoners = prisonersRaw.map((p) => ({
        id: p.prisonerId,
        name: p.name || "Unknown",
        email: "—",
        attendance: p.status,
        isPrisoner: true,
      }));

      setAttendanceData([...users, ...prisoners]);
      setAttendancePagination({
        page: res.data?.page || page,
        limit: res.data?.limit || limit,
        total: res.data?.total || 0,
      });
    } catch (err) {
      console.error("Error fetching attendance:", err);
      setAttendanceData([]);
    } finally {
      setAttLoading(false);
    }
  };

  // ================= HANDLE ATTENDANCE PAGE CHANGE =================
  const handleAttendanceChange = (page) => {
    if (selectedClass && selectedSession) {
      handleShowAttendance(selectedSession, page, attendancePagination.limit);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          Log Management
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
            }}
          >
            <ClockHistory size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
            >
              Log Management
            </Typography>
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              {pagination.total} total logs
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={2}
            py={8}
          >
            <Spinner
              animation="border"
              style={{
                color: "#0d47a1",
                width: 36,
                height: 36,
                borderWidth: 3,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Loading logs...
            </Typography>
          </Box>
        ) : (
          <Table hover className="mb-0 align-middle">
            <thead style={{ background: primaryGradient }}>
              <tr>
                {[
                  "#",
                  "IP Address",
                  "Reason",
                  "Deleted By (time)",
                  "Affected Role",
                  "Affected User",
                  "Total Collection",
                  "Action",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      color: "rgba(255,255,255,0.85)",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      padding: "13px 16px",
                      border: "none",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-5 text-muted">
                    No logs found.
                  </td>
                </tr>
              ) : (
                data.map((cls, idx) => (
                  <tr
                    key={cls._id}
                    style={{ borderBottom: "1px solid #f0f4ff" }}
                  >
                    <td
                      style={{
                        color: "#aab4cc",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {(pagination.page - 1) * pagination.limit + idx + 1}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {cls.ipAddress}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {cls.reason || "N/A"}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {cls.deletedBy || "N/A"}
                      <br />
                      <span style={{ fontSize: "0.7rem", color: "#aab4cc" }}>
                        {cls.deletedAt
                          ? new Date(cls.deletedAt).toDateString()
                          : ""}
                      </span>
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {cls.affectedUserRole}
                    </td>
                    <td>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "#1a237e",
                            display: "block",
                          }}
                        >
                          {cls.affectedUserName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem", color: "#7b8ab8" }}
                        >
                          {cls.affectedUserEmail}
                        </Typography>
                      </Box>
                    </td>
                    <td>
                      <span
                        className="badge px-3 py-2 rounded-pill"
                        style={{
                          background: "#e4eaf8",
                          color: "#1a237e",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        {cls?.affectedCollections?.length || 0}
                      </span>
                    </td>
                    <td>
                      <ActionBtn
                        tooltip="View Collections"
                        color="#0d47a1"
                        onClick={() =>
                          handleShowCollections(cls.affectedCollections)
                        }
                      >
                        <EyeFill size={16} />
                      </ActionBtn>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2.5}
            py={1.8}
            sx={{ borderTop: "1px solid #f0f4ff", background: "#fafbff" }}
          >
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Showing{" "}
              <b style={{ color: "#1a237e" }}>
                {(pagination.page - 1) * pagination.limit + 1}
              </b>
              –
              <b style={{ color: "#1a237e" }}>
                {Math.min(pagination.page * pagination.limit, pagination.total)}
              </b>{" "}
              of <b style={{ color: "#1a237e" }}>{pagination.total}</b>
            </Typography>
            <Pagination size="sm" className="mb-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={pagination.page === 1}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                style={{ border: "none", background: "transparent" }}
              />

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === pagination.page}
                    onClick={() => handlePageChange(pageNum)}
                    style={
                      pageNum === pagination.page
                        ? {
                            backgroundColor: "#1a237e",
                            borderColor: "#1a237e",
                            color: "#fff",
                          }
                        : { border: "none", background: "transparent" }
                    }
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === totalPages}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={pagination.page === totalPages}
                style={{ border: "none", background: "transparent" }}
              />
            </Pagination>
          </Box>
        )}
      </Box>

      {/* ================= COLLECTION DETAILS MODAL ================= */}
      <Modal
        show={showCollectionModal}
        onHide={() => setShowCollectionModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <Collection size={18} /> Collection Details
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          {selectedCollections.length > 0 ? (
            <Table hover className="mb-0 align-middle">
              <thead style={{ background: primaryGradient }}>
                <tr>
                  <th style={{ color: "#fff", padding: "10px 12px" }}>#</th>
                  <th style={{ color: "#fff", padding: "10px 12px" }}>
                    Collection Name
                  </th>
                  <th style={{ color: "#fff", padding: "10px 12px" }}>
                    Action
                  </th>
                  <th style={{ color: "#fff", padding: "10px 12px" }}>
                    Affected Count
                  </th>
                </tr>
              </thead>
              <tbody>
                {selectedCollections.map((item, index) => (
                  <tr key={index} style={{ borderBottom: "1px solid #f0f4ff" }}>
                    <td
                      style={{
                        color: "#aab4cc",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {index + 1}
                    </td>
                    <td style={{ fontWeight: 500, color: "#1a237e" }}>
                      {item.collection}
                    </td>
                    <td>
                      <span
                        className={`badge px-3 py-2 rounded-pill ${
                          item.action === "create"
                            ? "bg-success"
                            : item.action === "update"
                              ? "bg-warning text-dark"
                              : "bg-danger"
                        }`}
                        style={{ fontSize: "0.75rem" }}
                      >
                        {item.action}
                      </span>
                    </td>
                    <td>
                      <span
                        className="badge px-3 py-2 rounded-pill"
                        style={{
                          background: "#e4eaf8",
                          color: "#1a237e",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        {item.affectedCount}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Box textAlign="center" py={3}>
              <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                No collection data found.
              </Typography>
            </Box>
          )}
        </Modal.Body>
      </Modal>
    </Box>
  );
};

export default LogManagement;
